import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import Address from "./Address";
import { SERVICE_ISSUE_TYPES } from "./ServiceIssue";
import COLORS from "../colors.js";
import { deepFreeze, timestampToDate } from "../util/misc";

// The customer's copy of a service issue, written by
// functions/customerMirrors.js at c/{customerID}/serviceIssues/{issueID}.
// `stage` is computed there from fields the customer never sees (the work
// order, the review) -- see serviceIssueStage.
export const CUSTOMER_SERVICE_ISSUE_FIELDS = {
  projectID: "",
  saleID: "",
  itemID: "",
  quantity: 1,
  issueType: "",
  description: "",
  resolution: "",
  isResolved: false,
  isReviewed: false,
  isCustomerReported: false,
  reportedDate: null,
  completedDate: null,
  partsRequired: false,
  partsOrdered: false,
  partsOrderedDate: null,
  partsReceived: false,
  partsReceivedDate: null,
  addedToWorkOrderDate: null,
  serviceAddress: null,
  media: [],
  stage: "reported",

  created: null,
  modified: null,
};
deepFreeze(CUSTOMER_SERVICE_ISSUE_FIELDS);

export const CUSTOMER_SERVICE_ISSUE_STAGES = {
  reported: {
    order: 0,
    label: "Reported",
    icon: "add_circle",
    color: "info",
  },
  reviewed: {
    order: 1,
    label: "Under review",
    icon: "pending",
    color: "info",
  },
  parts_ordered: {
    order: 2,
    label: "Parts ordered",
    icon: "order",
    color: "warning",
  },
  parts_received: {
    order: 3,
    label: "Parts received",
    icon: "inventory",
    color: "warning",
  },
  scheduled: {
    order: 4,
    label: "Service scheduled",
    icon: "calendar",
    color: "warning",
  },
  completed: {
    order: 5,
    label: "Completed",
    icon: "check_circle",
    color: "positive",
  },
};
deepFreeze(CUSTOMER_SERVICE_ISSUE_STAGES);

const DATE_FIELDS = [
  "reportedDate",
  "completedDate",
  "partsOrderedDate",
  "partsReceivedDate",
  "addedToWorkOrderDate",
  "created",
  "modified",
];

export default class CustomerServiceIssue extends DataStructure {
  constructor(customerID, id, data = {}) {
    data = sanitize(data, CUSTOMER_SERVICE_ISSUE_FIELDS);
    DATE_FIELDS.forEach((field) => {
      data[field] = timestampToDate(data[field]);
    });
    super(data);

    this.customerID = customerID;
    this.id = id;
  }

  get stage() {
    const type =
      CUSTOMER_SERVICE_ISSUE_STAGES[this.data.stage] ||
      CUSTOMER_SERVICE_ISSUE_STAGES.reported;
    return { ...type, ...COLORS[type.color] };
  }

  get issueType() {
    const type = SERVICE_ISSUE_TYPES[this.data.issueType];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get icon() {
    return this.issueType ? this.issueType.icon : "issue";
  }

  get media() {
    return (this.data.media || []).map((doc) => new DocumentRef(doc));
  }

  get serviceAddress() {
    return this.data.serviceAddress
      ? new Address(this.data.serviceAddress)
      : null;
  }

  get project() {
    return this.data.projectID
      ? store.state.main.customerProjects[this.data.projectID] || null
      : null;
  }

  get item() {
    return this.data.itemID
      ? store.state.main.customerProjectItems[this.data.itemID] || null
      : null;
  }

  toString() {
    return this.issueType ? this.issueType.label : "Service Issue";
  }

  // Database

  get path() {
    return CustomerServiceIssue.getPath(this.customerID, this.id);
  }

  static getPath(customerID, id) {
    let path = `c/${customerID}/serviceIssues`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(issue) {
    return issue.data;
  }

  static fromFirestore(snapshot, options) {
    return new CustomerServiceIssue(
      snapshot.ref.parent.parent.id,
      snapshot.id,
      snapshot.data(options)
    );
  }
}
